import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Botao } from './base';

export interface ItemMenu {
  chave: string;
  rotulo: string;
  icone?: ReactNode;
  /** Item que representa o estado atual (workspace em uso): `aria-current` e cor de acento. */
  atual?: boolean;
  /** Ajustes do CRM 27/10 (C16), opt-in: ação destrutiva em vermelho ("Cancelar reunião"). */
  perigo?: boolean;
  /** Ajustes do CRM 27/10 (C16), opt-in: linha separadora antes do item. */
  separadorAntes?: boolean;
  aoClicar: () => void;
}

/** Item de destino para a tecla no menu aberto, ou -1 (função pura, testada em `tests/abas-teclado.test.ts`). */
export function itemDaTecla(atual: number, total: number, tecla: string): number {
  if (total <= 0) return -1;
  if (tecla === 'ArrowDown') return atual < 0 ? 0 : (atual + 1) % total;
  if (tecla === 'ArrowUp') return atual < 0 ? total - 1 : (atual - 1 + total) % total;
  if (tecla === 'Home') return 0;
  if (tecla === 'End') return total - 1;
  return -1;
}

const FOLGA_JANELA = 8;

/**
 * Deslocamento horizontal que mantém o painel inteiro dentro da janela (r7 visual M2: o popover de Colunas saía 21px à
 * esquerda a 360px). Função pura, testada em `tests/camada-flutuante.test.ts`.
 */
export function deslocamentoNaJanela(esquerda: number, largura: number, larguraJanela: number, folga = FOLGA_JANELA): number {
  if (largura + folga * 2 >= larguraJanela) return folga - esquerda;
  if (esquerda < folga) return folga - esquerda;
  const direita = esquerda + largura;
  if (direita > larguraJanela - folga) return larguraJanela - folga - direita;
  return 0;
}

/**
 * Posição vertical do painel (r8 visual B1: o painel de Colunas passava da base da janela a 1440x520 e a 360x640): abre
 * para baixo se couber; senão, para o lado com mais espaço; e a altura fica limitada a esse espaço (rolagem interna).
 * Função pura, testada em `tests/camada-flutuante.test.ts`.
 */
export function posicaoVertical(topoGatilho: number, baseGatilho: number, alturaPainel: number, alturaJanela: number, folga = FOLGA_JANELA, vao = 4): { paraCima: boolean; alturaMax: number } {
  const abaixo = alturaJanela - baseGatilho - vao - folga;
  const acima = topoGatilho - vao - folga;
  const paraCima = alturaPainel > abaixo && acima > abaixo;
  return { paraCima, alturaMax: Math.max(0, Math.floor(paraCima ? acima : abaixo)) };
}

/**
 * Posição do painel solto (no `body`, `position: fixed`) junto ao gatilho: embaixo, alinhado à esquerda do gatilho ou, com
 * `direita`, à direita dele; para cima quando falta espaço; sempre dentro da janela. Função pura, testada em
 * `tests/crm6-status-proposta.test.ts`.
 */
export function posicaoSolta(g: { top: number; bottom: number; left: number; right: number }, painel: { width: number; height: number }, janela: { width: number; height: number }, direita = false, vao = 4) {
  const esquerda = direita ? g.right - painel.width : g.left;
  const left = Math.round(esquerda + deslocamentoNaJanela(esquerda, painel.width, janela.width));
  const { paraCima, alturaMax } = posicaoVertical(g.top, g.bottom, painel.height, janela.height, FOLGA_JANELA, vao);
  const altura = Math.min(painel.height, alturaMax);
  return { left, top: Math.round(paraCima ? g.top - vao - altura : g.bottom + vao), alturaMax: painel.height > alturaMax ? alturaMax : null };
}

/**
 * Mecanismo comum das camadas flutuantes não modais (Menu, Popover, seletor de workspace): Esc fecha e devolve o foco ao
 * gatilho (sem fechar o modal de baixo), clique fora fecha, foco que sai da raiz (Tab) fecha, e o painel é deslocado para
 * nunca sair da janela. Lote crm6 (item 18): com `solto`, o painel vai para o `body` (portal) e fica fixo junto ao gatilho, por
 * cima de qualquer contêiner com rolagem ou corte (a lista de propostas); rolar a página o acompanha. O painel conta como
 * "dentro" para o clique e o foco.
 */
export function useCamadaFlutuante({ aberto, fechar, raiz, painel, gatilho, solto }: { aberto: boolean; fechar: () => void; raiz: RefObject<HTMLElement | null>; painel: RefObject<HTMLElement | null>; gatilho: () => HTMLElement | null | undefined; solto?: { direita?: boolean } }) {
  const ehSolto = !!solto;
  const direita = !!solto?.direita;
  useEffect(() => {
    if (!aberto) return;
    const dentro = (n: Node | null) => !!n && (!!raiz.current?.contains(n) || !!painel.current?.contains(n));
    const fora = (e: MouseEvent) => {
      if (!dentro(e.target as Node)) fechar();
    };
    const saiu = (e: FocusEvent) => {
      const para = e.relatedTarget as Node | null;
      if (para && !dentro(para)) fechar();
    };
    const el = raiz.current;
    const pa = ehSolto ? painel.current : null;
    document.addEventListener('mousedown', fora);
    el?.addEventListener('focusout', saiu);
    pa?.addEventListener('focusout', saiu);
    return () => {
      document.removeEventListener('mousedown', fora);
      el?.removeEventListener('focusout', saiu);
      pa?.removeEventListener('focusout', saiu);
    };
  }, [aberto, fechar, raiz, painel, ehSolto]);
  // Posição medida antes da pintura e aplicada direto no painel (sem estado: não há render extra).
  useLayoutEffect(() => {
    if (!aberto) return;
    const posicionar = () => {
      const p = painel.current;
      if (!p) return;
      if (ehSolto) {
        const g = (gatilho() ?? raiz.current)?.getBoundingClientRect();
        if (!g) return;
        Object.assign(p.style, { maxHeight: '', overflowY: '' });
        const r = posicaoSolta(g, { width: p.offsetWidth || p.getBoundingClientRect().width, height: p.scrollHeight }, { width: window.innerWidth, height: window.innerHeight }, direita);
        Object.assign(p.style, { left: `${r.left}px`, top: `${r.top}px`, ...(r.alturaMax !== null ? { maxHeight: `${r.alturaMax}px`, overflowY: 'auto' } : {}) });
        return;
      }
      Object.assign(p.style, { transform: '', top: '', bottom: '', marginBottom: '', maxHeight: '', overflowY: '' });
      const r = p.getBoundingClientRect();
      const dx = Math.round(deslocamentoNaJanela(r.left, r.width, window.innerWidth));
      if (dx) p.style.transform = `translateX(${dx}px)`;
      // Vertical: mede o gatilho (a raiz) e a altura natural do painel; abre para cima quando falta espaço embaixo.
      const g = (raiz.current ?? p.parentElement)?.getBoundingClientRect();
      if (!g) return;
      const { paraCima, alturaMax } = posicaoVertical(g.top, g.bottom, p.scrollHeight, window.innerHeight);
      if (paraCima) Object.assign(p.style, { top: 'auto', bottom: '100%', marginBottom: '4px' });
      if (p.scrollHeight > alturaMax) Object.assign(p.style, { maxHeight: `${alturaMax}px`, overflowY: 'auto' });
    };
    posicionar();
    window.addEventListener('resize', posicionar);
    // Solto: a rolagem (da página ou do contêiner) move o gatilho; o painel o acompanha.
    if (ehSolto) window.addEventListener('scroll', posicionar, true);
    return () => {
      window.removeEventListener('resize', posicionar);
      if (ehSolto) window.removeEventListener('scroll', posicionar, true);
    };
  }, [aberto, painel, raiz, ehSolto, direita, gatilho]);
  const aoTeclar = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      if (e.key !== 'Escape' || !aberto) return false;
      e.preventDefault();
      // Só a camada flutuante fecha (não o modal ou drawer de baixo).
      e.stopPropagation();
      fechar();
      gatilho()?.focus();
      return true;
    },
    [aberto, fechar, gatilho],
  );
  return { aoTeclar };
}

/**
 * Menu de ações do núcleo (r6 visual B7), no padrão de menu WAI-ARIA: o botão abre com clique, Enter, Espaço ou seta para baixo
 * (foco no primeiro item; seta para cima, no último); no menu, setas com volta, Home e End movem o foco; Esc fecha e devolve o
 * foco ao botão; Tab ou clique fora fecham. Os itens ficam fora da ordem de Tab (tabindex -1). `gatilho` troca o botão padrão
 * por um conteúdo próprio (seletor de workspace), com a mesma semântica.
 */
export function Menu({
  rotulo,
  icone,
  itens,
  className = '',
  gatilho,
  classeGatilho,
  classePainel = 'left-0 w-60',
  solto,
  ...dados
}: {
  rotulo: string;
  icone?: ReactNode;
  itens: ItemMenu[];
  className?: string;
  gatilho?: ReactNode;
  classeGatilho?: string;
  classePainel?: string;
  /** Lote crm6 (item 18), opt-in: o painel abre no `body` (portal), fixo junto ao gatilho, sem ser cortado pelo cartão ou tabela. */
  solto?: { direita?: boolean };
  [dado: `data-${string}`]: string;
}) {
  const [aberto, setAberto] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  const painel = useRef<HTMLDivElement>(null);
  const id = useId();
  const focoInicial = useRef(0);

  // Os itens ficam no painel (dentro da raiz ou, solto, no body).
  const botoes = () => [...(painel.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
  const elGatilho = useCallback(() => raiz.current?.querySelector<HTMLElement>('[aria-haspopup="menu"]'), []);
  const fecharSo = useCallback(() => setAberto(false), []);
  const { aoTeclar } = useCamadaFlutuante({ aberto, fechar: fecharSo, raiz, painel, gatilho: elGatilho, solto });

  useEffect(() => {
    if (!aberto) return;
    const id = requestAnimationFrame(() => painel.current?.querySelectorAll<HTMLElement>('[role="menuitem"]')[focoInicial.current]?.focus());
    return () => cancelAnimationFrame(id);
  }, [aberto]);

  function abrir(indice: number) {
    focoInicial.current = indice;
    setAberto(true);
  }
  function fechar(devolver: boolean) {
    setAberto(false);
    if (devolver) elGatilho()?.focus();
  }

  function noMenu(e: KeyboardEvent<HTMLDivElement>) {
    if (aoTeclar(e)) return;
    if (e.key === 'Tab') {
      // Rodada rlote-4 (VIS-M1): o menu solto fica no fim do `body` (portal); deixar o Tab ao navegador mandava o foco para fora
      // da ordem da página. Fecha e devolve o foco ao gatilho (Tab e Shift+Tab), e o próximo Tab segue a partir dele.
      if (solto) {
        e.preventDefault();
        return fechar(true);
      }
      return setAberto(false);
    }
    const lista = botoes();
    const vai = itemDaTecla(lista.indexOf(document.activeElement as HTMLElement), lista.length, e.key);
    if (vai < 0) return;
    e.preventDefault();
    lista[vai]?.focus();
  }

  const propsGatilho = {
    'aria-haspopup': 'menu' as const,
    'aria-expanded': aberto,
    'aria-controls': aberto ? id : undefined,
    onClick: () => (aberto ? fechar(false) : abrir(Math.max(0, itens.findIndex((i) => i.atual)))),
    onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => {
      if (aoTeclar(e)) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        abrir(e.key === 'ArrowDown' ? 0 : itens.length - 1);
      }
    },
  };

  if (itens.length === 0) return null;
  const conteudo = aberto && (
        <div ref={painel} id={id} role="menu" aria-label={rotulo} onKeyDown={noMenu} data-menu-solto={solto ? '' : undefined} className={`${solto ? 'fixed z-50' : 'absolute z-30 mt-1'} flex flex-col gap-0.5 rounded-lg border border-borda bg-sup-2 p-1.5 shadow-pop ${classePainel}`}>
          {itens.map((i) => [
            i.separadorAntes ? <hr key={`${i.chave}-sep`} aria-hidden className="my-1 border-borda" /> : null,
            <button
              key={i.chave}
              type="button"
              role="menuitem"
              tabIndex={-1}
              aria-current={i.atual || undefined}
              title={i.rotulo}
              onClick={() => {
                fechar(true);
                i.aoClicar();
              }}
              className={`flex min-h-10 min-w-0 items-center gap-2 rounded px-2 text-left text-sm hover:bg-sup-3 focus:bg-sup-3 ${i.atual ? 'font-semibold text-acento-forte' : i.perigo ? 'text-perigo' : 'text-tinta-1'}`}
            >
              {i.icone}
              <span className="min-w-0 truncate" title={i.rotulo}>
                {i.rotulo}
              </span>
            </button>,
          ])}
        </div>
  );
  return (
    <div ref={raiz} className={`relative ${className}`}>
      {gatilho ? (
        <button type="button" aria-label={rotulo} className={classeGatilho} {...propsGatilho} {...dados}>
          {gatilho}
        </button>
      ) : (
        <Botao icone={icone} {...propsGatilho} {...dados}>
          {rotulo}
        </Botao>
      )}
      {conteudo && (solto ? createPortal(conteudo, document.body) : conteudo)}
    </div>
  );
}

/**
 * Painel flutuante não modal (r7 visual M2), como o de Colunas da tabela: o botão leva `aria-expanded` e `aria-controls`;
 * Esc fecha e devolve o foco ao botão; clique fora ou Tab para fora fecham; o painel nunca sai da janela.
 */
export function Popover({ rotulo, icone, children, classePainel = 'right-0 w-56', texto, ...dados }: { rotulo: string; icone: ReactNode; children: ReactNode; classePainel?: string; /** C1: botão com texto à vista ("Mais filtros (2)"), na variante secundária, em vez de só o ícone. */ texto?: ReactNode; [dado: `data-${string}`]: string }) {
  const [aberto, setAberto] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  const painel = useRef<HTMLDivElement>(null);
  const id = useId();
  const fechar = useCallback(() => setAberto(false), []);
  const gatilho = useCallback(() => raiz.current?.querySelector<HTMLElement>(':scope > button'), []);
  const { aoTeclar } = useCamadaFlutuante({ aberto, fechar, raiz, painel, gatilho });
  return (
    <div ref={raiz} className="relative" onKeyDown={aoTeclar}>
      {texto ? (
        <Botao aria-expanded={aberto} aria-controls={aberto ? id : undefined} onClick={() => setAberto((x) => !x)} icone={icone} {...dados}>
          {texto}
        </Botao>
      ) : (
        <Botao variante="fantasma" tamanho="sm" aria-label={rotulo} title={rotulo} aria-expanded={aberto} aria-controls={aberto ? id : undefined} onClick={() => setAberto((x) => !x)} icone={icone} {...dados} />
      )}
      {aberto && (
        <div ref={painel} id={id} role="group" aria-label={rotulo} className={`absolute z-30 mt-1 rounded-lg border border-borda bg-sup-2 p-2 shadow-pop ${classePainel}`}>
          {children}
        </div>
      )}
    </div>
  );
}
